import { useMemo, useState, type ReactElement } from 'react';
import {
  phaseTypeLabel,
  type IntensityDistribution,
  type PhaseType,
  type SeasonPhase,
  type SeasonPlan,
} from '@/lib/season';
import { MetricTooltip } from '@/components/MetricTooltip';

/**
 * Timeline visual del macrociclo (M2.2).
 *
 * Decisiones de diseño:
 *
 * 1. Escala = duración (días) → ancho proporcional con `flex-grow`.
 *    En lugar de un valor `px` calculado, usamos `flex` con `flex-grow`
 *    proporcional al número de días de cada fase. Así la timeline se adapta
 *    al ancho disponible (responsive sin media queries) y mantiene la
 *    proporcionalidad entre fases. En viewports estrechos, los bloques se
 *    quedan estrechos pero conservan el orden y los colores.
 *
 *    En viewports muy pequeños se haría inmanejable: a partir de `sm:` se
 *    usa la timeline horizontal; en mobile (<640px) caemos a una lista
 *    vertical con los mismos colores. Esto evita scroll horizontal y
 *    cumple con WCAG (1.4.10 reflow).
 *
 * 2. Fase actual = aquella que contiene `new Date()` (UTC) entre
 *    `startDateISO` y `endDateISO` inclusive. Si el evento ya pasó, ninguna
 *    fase se marca como actual y se muestra un banner discreto en el header
 *    del SeasonPage (no aquí). Si hoy es ANTES de la primera fase
 *    (improbable, pero posible si el plan se crea con margen), tampoco se
 *    destaca nada.
 *
 * 3. Eventos pasados: el bloque sigue renderizándose con su color, pero sin
 *    borde grueso. El header del SeasonPage es quien avisa al usuario;
 *    SeasonTimeline se mantiene puro y solo refleja datos.
 *
 * 4. Accesibilidad: lista semántica `<ol>` con `aria-label` global, cada
 *    bloque es un `<li>` con `aria-label` descriptivo ("Fase Base 1, del
 *    1 de junio al 15 de julio, 10 horas semanales") para lectores de
 *    pantalla. Los colores tienen suficiente contraste AA y NUNCA son el
 *    único discriminador — el nombre de la fase también aparece como texto.
 */
export function SeasonTimeline({ plan }: { plan: SeasonPlan }): ReactElement {
  const phases = useMemo(
    () => [...plan.phases].sort((a, b) => a.idx - b.idx),
    [plan.phases],
  );

  const currentIdx = useMemo(() => findCurrentPhaseIndex(phases), [phases]);

  if (phases.length === 0) {
    return (
      <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Este plan no tiene fases definidas. Crea uno nuevo para regenerarlas.
      </p>
    );
  }

  // Pre-calculamos los días por fase para usarlos como flex-grow proporcional.
  const phasesWithDays = phases.map((p) => ({
    phase: p,
    days: Math.max(1, daysBetween(p.startDateISO, p.endDateISO)),
  }));

  return (
    <section aria-label="Timeline de fases de la temporada" className="space-y-4">
      {/* Timeline horizontal (sm+) */}
      <ol
        className="hidden gap-1 overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-sm sm:flex"
        aria-label="Fases del macrociclo en orden temporal"
      >
        {phasesWithDays.map(({ phase, days }, i) => (
          <li
            key={phase.id}
            style={{ flexGrow: days }}
            className="min-w-0 flex-shrink basis-0"
          >
            <PhaseBlock
              phase={phase}
              isCurrent={i === currentIdx}
              orientation="horizontal"
            />
          </li>
        ))}
      </ol>

      {/* Lista vertical (mobile) */}
      <ol
        className="space-y-2 sm:hidden"
        aria-label="Fases del macrociclo en orden temporal (vista mobile)"
      >
        {phases.map((phase, i) => (
          <li key={phase.id}>
            <PhaseBlock
              phase={phase}
              isCurrent={i === currentIdx}
              orientation="vertical"
            />
          </li>
        ))}
      </ol>

      {/* Leyenda de colores */}
      <PhaseLegend />
    </section>
  );
}

// ---------------------------------------------------------------------------
// PhaseBlock
// ---------------------------------------------------------------------------

function PhaseBlock({
  phase,
  isCurrent,
  orientation,
}: {
  phase: SeasonPhase;
  isCurrent: boolean;
  orientation: 'horizontal' | 'vertical';
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const colors = phaseColorClasses(phase.phaseType);
  const dateRange = formatDateRange(phase.startDateISO, phase.endDateISO);
  const ariaLabel =
    `Fase ${phase.name} (${phaseTypeLabel(phase.phaseType)}), ` +
    `del ${formatDateLong(phase.startDateISO)} al ${formatDateLong(phase.endDateISO)}, ` +
    `${formatHoursPerWeek(phase.targetWeeklyHours)}` +
    (isCurrent ? '. Fase actual.' : '');

  const baseLayout =
    orientation === 'horizontal'
      ? 'flex h-full min-h-[7rem] flex-col justify-between p-3'
      : 'flex flex-col gap-1 p-3';
  const ringClass = isCurrent
    ? 'ring-2 ring-offset-2 ring-slate-900'
    : 'ring-1 ring-inset ring-black/5';

  return (
    <article
      aria-label={ariaLabel}
      aria-current={isCurrent ? 'step' : undefined}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
      tabIndex={0}
      className={`relative rounded-md ${baseLayout} ${colors.bg} ${colors.text} ${ringClass} transition focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700`}
    >
      <header>
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="truncate text-sm font-semibold">{phase.name}</h3>
          {isCurrent && (
            <span className="rounded-full bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              Hoy
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide opacity-80">
          {phaseTypeLabel(phase.phaseType)}
        </p>
      </header>

      <div className="mt-2 space-y-0.5 text-xs">
        <p className="font-medium">{dateRange}</p>
        <p>
          <MetricTooltip metric="targetWeeklyHours">
            {formatHoursPerWeek(phase.targetWeeklyHours)}
          </MetricTooltip>
        </p>
      </div>

      {phase.focus && (
        <p
          className={`mt-1 text-xs ${expanded ? 'whitespace-pre-wrap' : 'line-clamp-2'} ${
            orientation === 'horizontal' ? '' : ''
          }`}
          title={phase.focus}
        >
          {phase.focus}
        </p>
      )}

      {expanded && phase.intensityDistribution && hasAnyZone(phase.intensityDistribution) && (
        <div className="mt-2 rounded bg-black/10 px-2 py-1 text-[11px]">
          <span className="block font-semibold uppercase tracking-wide opacity-80">
            <MetricTooltip metric="intensityDistribution">Distribución</MetricTooltip>
          </span>
          <span className="block">{formatIntensity(phase.intensityDistribution)}</span>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Leyenda
// ---------------------------------------------------------------------------

function PhaseLegend(): ReactElement {
  return (
    <div
      aria-label="Leyenda de colores"
      className="flex flex-wrap items-center gap-2 text-xs text-slate-700"
    >
      <span className="font-medium">Tipos:</span>
      {(['base', 'build', 'peak', 'taper', 'race_week', 'recovery', 'transition'] as PhaseType[]).map(
        (t) => {
          const c = phaseColorClasses(t);
          return (
            <span
              key={t}
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 ${c.bg} ${c.text}`}
            >
              <span
                aria-hidden="true"
                className={`inline-block h-2 w-2 rounded-full ${c.dot}`}
              />
              {phaseTypeLabel(t)}
            </span>
          );
        },
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers de color / formato (puros)
// ---------------------------------------------------------------------------

/**
 * Devuelve clases Tailwind con contraste AA (WCAG 2.1) para cada tipo de fase.
 * Cada color usa una versión `-600`/`-700` de fondo con texto blanco — todos
 * superan 4.5:1 (texto pequeño) según el ratio calculado con el contraste de
 * la paleta Tailwind v3.
 */
export function phaseColorClasses(t: PhaseType): {
  bg: string;
  text: string;
  dot: string;
} {
  switch (t) {
    case 'base':
      return { bg: 'bg-cyan-700', text: 'text-white', dot: 'bg-cyan-200' };
    case 'build':
      return { bg: 'bg-amber-600', text: 'text-white', dot: 'bg-amber-200' };
    case 'peak':
      return { bg: 'bg-orange-600', text: 'text-white', dot: 'bg-orange-200' };
    case 'taper':
      return { bg: 'bg-rose-600', text: 'text-white', dot: 'bg-rose-200' };
    case 'race_week':
      return { bg: 'bg-red-700', text: 'text-white', dot: 'bg-red-200' };
    case 'recovery':
      return { bg: 'bg-slate-500', text: 'text-white', dot: 'bg-slate-200' };
    case 'transition':
      return { bg: 'bg-slate-600', text: 'text-white', dot: 'bg-slate-200' };
  }
}

/**
 * Devuelve el índice de la fase que contiene "hoy" (UTC). -1 si ninguna lo
 * contiene (evento pasado, plan futuro, o gaps entre fases).
 *
 * Comparamos por timestamp UTC para evitar sesgos de DST: tratamos
 * `startDateISO`/`endDateISO` como YYYY-MM-DD interpretado a medianoche UTC,
 * y "hoy" como `Date.now()`. La fase es válida si
 * `start <= now < end + 1 día` (la fase incluye su último día completo).
 */
export function findCurrentPhaseIndex(phases: SeasonPhase[]): number {
  const now = Date.now();
  for (let i = 0; i < phases.length; i++) {
    const start = parseISODateUTC(phases[i].startDateISO);
    const end = parseISODateUTC(phases[i].endDateISO);
    if (start == null || end == null) continue;
    // end es la medianoche UTC del último día — sumamos 24h para incluirlo.
    const endExclusive = end + 24 * 60 * 60 * 1000;
    if (now >= start && now < endExclusive) {
      return i;
    }
  }
  return -1;
}

function parseISODateUTC(iso: string): number | null {
  // Acepta YYYY-MM-DD o ISO completo. Si no tiene 'T', le añadimos
  // 'T00:00:00Z' para forzar UTC y evitar interpretaciones locales.
  const normalized = iso.includes('T') ? iso : `${iso}T00:00:00Z`;
  const t = Date.parse(normalized);
  return Number.isFinite(t) ? t : null;
}

function daysBetween(startISO: string, endISO: string): number {
  const s = parseISODateUTC(startISO);
  const e = parseISODateUTC(endISO);
  if (s == null || e == null) return 1;
  return Math.max(1, Math.round((e - s) / (24 * 60 * 60 * 1000)) + 1);
}

function formatDateRange(startISO: string, endISO: string): string {
  return `${formatDateShort(startISO)} — ${formatDateShort(endISO)}`;
}

function formatDateShort(iso: string): string {
  const t = parseISODateUTC(iso);
  if (t == null) return iso;
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(t));
  } catch {
    return iso;
  }
}

function formatDateLong(iso: string): string {
  const t = parseISODateUTC(iso);
  if (t == null) return iso;
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(t));
  } catch {
    return iso;
  }
}

function formatHoursPerWeek(h: number): string {
  const rounded = Number.isInteger(h) ? `${h}` : h.toFixed(1);
  return `${rounded} h/sem`;
}

function hasAnyZone(d: IntensityDistribution): boolean {
  return (
    (d.z1 ?? 0) > 0 ||
    (d.z2 ?? 0) > 0 ||
    (d.z3 ?? 0) > 0 ||
    (d.z4 ?? 0) > 0 ||
    (d.z5 ?? 0) > 0
  );
}

function formatIntensity(d: IntensityDistribution): string {
  const parts: string[] = [];
  if (d.z1 != null) parts.push(`Z1 ${d.z1}%`);
  if (d.z2 != null) parts.push(`Z2 ${d.z2}%`);
  if (d.z3 != null) parts.push(`Z3 ${d.z3}%`);
  if (d.z4 != null) parts.push(`Z4 ${d.z4}%`);
  if (d.z5 != null) parts.push(`Z5 ${d.z5}%`);
  return parts.join(' · ');
}
